import {
  NO_KEYBOARD_LAYOUT,
  type ReplacementDraft,
  type ValidatedAddress,
} from "@/pages/Replacement/replacement.types";
import {
  LCR_DELIVERY_METHOD,
  type LcrDeliveryMethod,
  type LcrOrder,
  type LcrReplacedDevice,
  type LcrSelectedDevice,
  type LcrShippingAddress,
} from "@/services/orders/lcr-order.types";
import { presentAddressValue } from "@/services/shipment-options/shipment-address.mapper";
import type { ShipmentDeliveryAddress } from "@/services/shipment-options/shipment-options.types";
import {
  REPLACEMENT_ORDER_ERROR_CODE,
  type ReplacementOrderErrorCode,
} from "./replacement-order.constants";
import { ReplacementOrderSubmitError } from "./replacement-order-submit-error";
import type {
  ReplacementOrderAddress,
  ReplacementOrderContext,
  ReplacementOrderDelivery,
  ReplacementOrderRequest,
  ReplacementOrderResult,
} from "./replacement-order.types";

const toOrderAddress = (saved: ValidatedAddress): ReplacementOrderAddress => ({
  address: saved.address,
  ...(saved.deliveryInstructions ? { deliveryInstructions: saved.deliveryInstructions } : {}),
});

const toDelivery = (
  draft: ReplacementDraft,
  pickupBuildingAddress?: ShipmentDeliveryAddress
): ReplacementOrderDelivery => {
  if (draft.deliveryMethod === "ship") {
    if (!draft.shippingAddress) {
      throw new Error("Cannot place a ship replacement order without a saved shipping address");
    }
    return { method: "ship", shippingAddress: toOrderAddress(draft.shippingAddress) };
  }
  if (draft.deliveryMethod === "pickup") {
    if (!draft.pickup) {
      throw new Error("Cannot place a pickup replacement order without a chosen building");
    }
    if (!pickupBuildingAddress) {
      throw new Error("Cannot place a pickup replacement order without the building's address");
    }
    return {
      method: "pickup",
      pickup: draft.pickup,
      buildingAddress: pickupBuildingAddress,
      ...(draft.homeAddress ? { homeAddress: toOrderAddress(draft.homeAddress) } : {}),
    };
  }
  throw new Error("Cannot place a replacement order without a delivery method");
};

export const toReplacementOrderRequest = (
  draft: ReplacementDraft,
  { pickupBuildingAddress, ...context }: ReplacementOrderContext
): ReplacementOrderRequest => {
  if (!draft.deviceId) {
    throw new Error("Cannot place a replacement order without a chosen device");
  }
  const keyboardLayout =
    draft.keyboardLayout && draft.keyboardLayout !== NO_KEYBOARD_LAYOUT
      ? draft.keyboardLayout
      : undefined;
  return {
    ...context,
    deviceId: draft.deviceId,
    ...(keyboardLayout ? { keyboardLayout } : {}),
    delivery: toDelivery(draft, pickupBuildingAddress),
  };
};

/** The website's `SubmitReplacementOrderRequest`; the backend fills `fullName`, and the phone for a pickup, from OUI. */
export type SubmitShippingAddress = Omit<LcrShippingAddress, "fullName" | "phoneNumber"> & {
  phoneNumber?: string;
};

export interface SubmitReplacementOrderBody {
  clientRequestId: string;
  serialNumber: string;
  newHardware: { laptop: string };
  selectedDevice: LcrSelectedDevice;
  replacedDevice: LcrReplacedDevice;
  preferredKeyboardLayout?: string;
  deliveryMethod: LcrDeliveryMethod;
  shippingAddress: SubmitShippingAddress;
}

const BUILDING_POSTAL_FIELDS = ["line1", "line2", "line3", "city", "state", "zip", "country"] as const;

type BuildingPostalAddress = Partial<Pick<ShipmentDeliveryAddress, (typeof BUILDING_POSTAL_FIELDS)[number]>>;

const presentBuildingAddress = (address: ShipmentDeliveryAddress): BuildingPostalAddress =>
  Object.fromEntries(
    BUILDING_POSTAL_FIELDS.map((field) => [field, presentAddressValue(address[field])]).filter(
      ([, value]) => value !== undefined
    )
  );

const toSubmitDelivery = (
  delivery: ReplacementOrderDelivery
): Pick<SubmitReplacementOrderBody, "deliveryMethod" | "shippingAddress"> => {
  if (delivery.method === "pickup") {
    const { line1, country, ...rest } = presentBuildingAddress(delivery.buildingAddress);
    if (!line1 || !country) {
      throw new Error("Cannot submit a pickup replacement order without the building's address");
    }
    return {
      deliveryMethod: delivery.pickup.deliveryMethod,
      shippingAddress: { ...rest, line1, country, deliveryBuilding: delivery.pickup.buildingCode },
    };
  }
  const { address, deliveryInstructions } = delivery.shippingAddress;
  const { line1 } = address;
  if (!line1) {
    throw new Error("Cannot submit a ship replacement order without an address line");
  }
  return {
    deliveryMethod: LCR_DELIVERY_METHOD.shipToHome,
    shippingAddress: { ...address, line1, ...(deliveryInstructions ? { deliveryInstructions } : {}) },
  };
};

export const toSubmitReplacementOrderBody = (
  request: ReplacementOrderRequest
): SubmitReplacementOrderBody => ({
  clientRequestId: request.clientRequestId,
  serialNumber: request.serialNumber,
  newHardware: { laptop: request.deviceId },
  selectedDevice: request.selectedDevice,
  replacedDevice: request.replacedDevice,
  ...(request.keyboardLayout ? { preferredKeyboardLayout: request.keyboardLayout } : {}),
  ...toSubmitDelivery(request.delivery),
});

export const toReplacementOrderResult = (order: Pick<LcrOrder, "orderId" | "lcrStatus">): ReplacementOrderResult => ({
  orderId: order.orderId,
  lcrStatus: order.lcrStatus,
});

interface SubmitErrorBody {
  code?: string;
  message?: string;
  orderId?: string;
}

const KNOWN_ERROR_CODES: readonly string[] = Object.values(REPLACEMENT_ORDER_ERROR_CODE);

const isKnownErrorCode = (code?: string): code is ReplacementOrderErrorCode =>
  code !== undefined && KNOWN_ERROR_CODES.includes(code);

export const toReplacementOrderSubmitError = (
  status: number,
  body?: SubmitErrorBody
): ReplacementOrderSubmitError => {
  const message = body?.message ?? `Replacement order API error: ${status}`;
  const code = body?.code;
  if (!isKnownErrorCode(code)) {
    return new ReplacementOrderSubmitError(REPLACEMENT_ORDER_ERROR_CODE.unknown, message);
  }
  return new ReplacementOrderSubmitError(code, message, body?.orderId);
};
